import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Third-party packages.
import * as dayjs from 'dayjs';
import * as relativeTime from 'dayjs/plugin/relativeTime';

import TableCSVExport from '../../../component/TableCSVExport';

import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Helper.
// Not used in this component

// Types.
import Agent from '../../../types/instance/Agent';

// Constants.
import { DATA_FAILED, DATA_EMPTY, DATA_LOADING } from '../../../component/Constants';

// Components.
import ReactTable from '../../../component/ReactTable';
import { getDefaultPageSize } from "../../../helpers/utils";
import SearchInput from '../../../component/SearchInput';

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(relativeTime.default);

function List(props: { loading: number, agents: Agent[], refresh: Function }) {
    const [tableFilter, setTableFilter] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);

    function refresh() {
        props.refresh();
    }

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data: Agent[] = useMemo(() => props.agents.filter(agent => (agent.name.toLowerCase().includes(tableFilter.toLowerCase()))), [props.agents, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: 'Name',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: 'string',
            Cell: ({ cell }) => (
                <React.Fragment>
                    {cell.row.values.name}
                </React.Fragment>
            )
        },
        {
            Header: 'Version',
            accessor: 'version',
            defaultCanSort: true,
            sortType: 'alphanumeric'
        },
        {
            Header: 'Hostname',
            accessor: 'hostname',
            defaultCanSort: true,
            sortType: 'alphanumeric'
        },
        {
            Header: 'OS',
            accessor: 'os',
            defaultCanSort: true,
            sortType: 'alphanumeric'
        },
        {
            Header: 'Remote Host',
            accessor: 'remotehost',
            defaultCanSort: true,
            sortType: 'alphanumeric'
        },
        {
            Header: 'Datasources',
            accessor: 'datasourceCount',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                <React.Fragment>
                    {cell.row.values.datasourceCount || 0}
                </React.Fragment>
            )
        },
        {
            Header: 'Hosts',
            accessor: 'hostCount',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                <React.Fragment>
                    {cell.row.values.hostCount || 0}
                </React.Fragment>
            )
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }) => (
                <React.Fragment>
                    {!(cell.row.values.name === 'default' && cell.row.values.id === 1) && (
                        <Link to={`/admin/agents/delete/${cell.row.values.id}`} role="button" className="btn btn-sm btn-danger">
                            <i className="fal fa-trash-alt fa-fw"></i><span>Delete</span>
                        </Link>
                    )}
                </React.Fragment>
            )
        }
    ], []);

    const tableInstance = useTable({
        columns,
        data,
        initialState: { hiddenColumns: ['id'], pageIndex: 0, pageSize: getDefaultPageSize(), sortBy: [{ id: 'name', desc: false }] }
    },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-light fa-circle-arrow-up-right fa-fw" aria-hidden="true"></i>
                    Agents
                    <span className="badge bg-info" data-tip="Total agent count">{props.agents.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseAgents" aria-expanded="false" aria-controls="collapseAgents"></i>
                </div>
                <div id="collapseAgents" className="card-body collapse show">
                    {props.loading === DATA_EMPTY ? (
                        <div className="w-100 text-center text-muted my-3">
                            <i className="fal fa-ban fa-fw fa-2x"></i>
                            <p>
                                <button type="button" className="btn btn-link" onClick={refresh}>No Agents Found - Click to Refresh</button>
                            </p>
                        </div>
                    ) : (
                        props.loading === DATA_FAILED ? (
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x text-danger"></i>
                                <p>
                                    <button type="button" className="btn btn-link" onClick={refresh}>Failed to Retrieve Agents - Click to Refresh</button>
                                </p>
                            </div>
                        ) : (
                            <React.Fragment>
                                <div className="row row-cols-1 row-cols-md-2 table-search">
                                    <div className="col col-md-10">
                                        <button onClick={() => setShowModal(true)} className="btn btn-sm btn-success">
                                            <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                        </button>
                                        <TableCSVExport
                                            tableInstance={tableInstance}
                                            filename="Agents"
                                        />
                                    </div>
                                    <div className="col col-md-2">
                                        <SearchInput
                                            value={tableFilter}
                                            onChange={(e) => setTableFilter(e.target.value)}
                                            onClear={clearTableFilter}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <ReactTable
                                    getTableProps={getTableProps}
                                    getTableBodyProps={getTableBodyProps}
                                    headerGroups={headerGroups}
                                    prepareRow={prepareRow}
                                    page={page}
                                    canPreviousPage={canPreviousPage}
                                    canNextPage={canNextPage}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    gotoPage={gotoPage}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    setPageSize={setPageSize}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    dataLength={data.length}
                                    error={null}
                                    loading={props.loading === DATA_LOADING}
                                />
                            </React.Fragment>
                        )
                    )}
                </div>
            </div>

            {/* Modal for Create Instructions */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Install Remote Agent</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body" style={{ padding: '2rem' }}>
                                <p>Agents can be installed on Linux or Kubernetes:</p>
                                <ul>
                                    <li><a href="https://docs.dbmarlin.com/docs/Getting-Started/agent-installation/installation-on-linux" target="_blank" rel="noopener noreferrer">Installation on Linux</a></li>
                                    <li><a href="https://docs.dbmarlin.com/docs/Getting-Started/agent-installation/installation-on-kubernetes" target="_blank" rel="noopener noreferrer">Installation on Kubernetes</a></li>
                                </ul>
                                <p>Once installed, the agent will automatically register with DBmarlin.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showModal && <div className="modal-backdrop fade show"></div>}
        </div>
    )
}

List.propTypes = {
    refresh: PropTypes.func.isRequired,
};

export default List;